// @ts-nocheck
/**
 * API client for the QoE Workbench backend.
 * Mirrors utils/sessionsApi.js: fetch + credentials:'include' + x-api-key header.
 * The session JWT cookie is set globally by App.js.
 */

const BASE = import.meta.env.VITE_BACKEND_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

const jsonHeaders = {
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
};

export async function initWorkbench(mappingSessionId) {
  const res = await fetch(`${BASE}/qoe/workbench/${mappingSessionId}/init`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.detail || `Init workbench failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function getWorkbench(mappingSessionId) {
  const res = await fetch(`${BASE}/qoe/workbench/${mappingSessionId}`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.detail || `Get workbench failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Extract a human-readable message from a FastAPI error body.
 * FastAPI may return `detail` as either a string or a structured dict (e.g.
 * {code, message, missing}). We surface .message when present, otherwise
 * fall back to the string form, otherwise to the status text.
 */
function messageFromDetail(detail, status) {
  if (!detail) return `Request failed (${status})`;
  if (typeof detail === 'string') return detail;
  if (typeof detail.message === 'string') return detail.message;
  if (typeof detail.detail === 'string') return detail.detail;
  return `Request failed (${status})`;
}

export async function getAdjustedIncomeStatement(mappingSessionId) {
  const res = await fetch(
    `${BASE}/qoe/workbench/${mappingSessionId}/adjusted-income-statement`,
    { credentials: 'include', headers: { 'x-api-key': API_KEY } }
  );
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.detail || `Get IS failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function getAccountDrilldown(mappingSessionId, accountName) {
  const encoded = encodeURIComponent(accountName);
  const res = await fetch(`${BASE}/qoe/workbench/${mappingSessionId}/account/${encoded}`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.detail || `Get drill-down failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function getTrend(mappingSessionId) {
  const res = await fetch(`${BASE}/qoe/workbench/${mappingSessionId}/trend`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.detail || `Get trend failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function getConcentration(mappingSessionId) {
  const res = await fetch(`${BASE}/qoe/workbench/${mappingSessionId}/concentration`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.detail || `Get concentration failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function getPunchList(mappingSessionId) {
  const res = await fetch(`${BASE}/qoe/workbench/${mappingSessionId}/punch-list`, {
    credentials: 'include',
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.detail || `Get punch list failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function patchAdjustment(adjustmentId, body) {
  const res = await fetch(`${BASE}/qoe/workbench/adjustments/${adjustmentId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    const detail = payload?.detail;
    const err = new Error(messageFromDetail(detail, res.status));
    err.status = res.status;
    err.detail = detail;
    err.code = (detail && typeof detail === 'object' && detail.code) || null;
    err.missing = (detail && typeof detail === 'object' && detail.missing) || null;
    throw err;
  }
  return res.json();
}
